import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

const MAX_NOTE_LENGTH = 1000;

const MAX_IMAGE_FILE_SIZE_MB = 8;
const MAX_IMAGE_WIDTH = 1200;
const MAX_IMAGE_HEIGHT = 900;
const IMAGE_COMPRESSION_QUALITY = 0.75;

const resizeImageFileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      image.src = reader.result;
    };

    reader.onerror = () => {
      reject(
        new Error('Impossible de lire le fichier image.')
      );
    };

    image.onload = () => {
      let { width, height } = image;

      if (
        width > MAX_IMAGE_WIDTH ||
        height > MAX_IMAGE_HEIGHT
      ) {
        const widthRatio = MAX_IMAGE_WIDTH / width;
        const heightRatio = MAX_IMAGE_HEIGHT / height;
        const ratio = Math.min(widthRatio, heightRatio);

        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');

      if (!context) {
        reject(
          new Error(
            'Impossible de préparer la compression de l’image.'
          )
        );
        return;
      }

      context.drawImage(image, 0, 0, width, height);

      const compressedDataUrl = canvas.toDataURL(
        'image/jpeg',
        IMAGE_COMPRESSION_QUALITY
      );

      resolve(compressedDataUrl);
    };

    image.onerror = () => {
      reject(
        new Error(
          'Le fichier sélectionné ne peut pas être chargé comme image.'
        )
      );
    };

    reader.readAsDataURL(file);
  });
};

const formatNoteDate = (value) => {
  if (!value) {
    return 'Date inconnue';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Date inconnue';
  }

  return date.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const StationNotesModal = ({
  open = false,
  onClose,
  stationId = '',
  stationType = '',
  stationData,
  notes = [],
  isLoading = false,
  error = '',
  onCreateNote,
  onDeleteNote,
  onStationDataChange,
  onOpenStationDetails,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isCreating, setIsCreating] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [profileDraft, setProfileDraft] = useState({
    description: '',
    photoUrl: '',
  });

  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');



  const initialDescription = useMemo(() => {
    return String(stationData?.description || '');
  }, [stationData]);

  const initialPhotoUrl = useMemo(() => {
    return String(stationData?.photoUrl || '');
  }, [stationData]);

  const stationDisplayName =
    String(
      stationData?.name ||
      stationData?.stationName ||
      stationData?.displayName ||
      ''
    ).trim() || String(stationId || '');

  useEffect(() => {
    if (open) {
      setProfileDraft({
        description: initialDescription,
        photoUrl: initialPhotoUrl,
      });

      setProfileError('');
    }

    if (!open) {
      setIsCreating(false);
      setNoteContent('');
      setIsPrivate(false);
      setActionError('');
      setIsSaving(false);
      setIsProfileSaving(false);
      setProfileError('');
    }
  }, [
    open,
    initialDescription,
    initialPhotoUrl,
  ]);

  const handleClose = () => {
    if (isSaving || isProfileSaving) {
      return;
    }

    onClose?.();
  };

  const handleOpenStationDetailsModal = () => {
    if (isSaving || isProfileSaving) {
      return;
    }

    if (!stationType || !stationId) {
      setActionError(
        'Impossible d’ouvrir la fiche : aucune station valide n’est sélectionnée.'
      );
      return;
    }

    if (typeof onOpenStationDetails !== 'function') {
      setActionError(
        'La fiche station n’est pas disponible.'
      );
      return;
    }

    setActionError('');

    /*
     * On ferme la modal de notes avant d’ouvrir la fiche station.
     */
    onClose?.();

    onOpenStationDetails({
      table: String(stationType).toLowerCase(),
      id: String(stationId),
    });
  };

  const handleProfileFieldChange = (field, value) => {
    setProfileError('');

    setProfileDraft((previousDraft) => ({
      ...previousDraft,
      [field]: value,
    }));
  };

  const handleProfilePhotoFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];

    /*
     * Permet de sélectionner deux fois le même fichier.
     */
    event.target.value = '';

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      setProfileError(
        'Le fichier sélectionné doit être une image.'
      );
      return;
    }

    const fileSizeInMb = selectedFile.size / 1024 / 1024;

    if (fileSizeInMb > MAX_IMAGE_FILE_SIZE_MB) {
      setProfileError(
        `L’image est trop lourde. Taille maximale autorisée : ${MAX_IMAGE_FILE_SIZE_MB} Mo.`
      );
      return;
    }

    try {
      const compressedImage =
        await resizeImageFileToDataUrl(selectedFile);

      handleProfileFieldChange('photoUrl', compressedImage);
    } catch (err) {
      setProfileError(
        err?.message ||
        'Impossible de préparer l’image sélectionnée.'
      );
    }
  };

  const handleRemoveProfilePhoto = () => {
    if (isProfileSaving) {
      return;
    }

    handleProfileFieldChange('photoUrl', '');
  };

  const handleSaveProfilePart = async () => {
    if (!onStationDataChange) {
      setProfileError(
        'La modification de la fiche station n’est pas disponible.'
      );
      return;
    }

    setIsProfileSaving(true);
    setProfileError('');

    try {
      const savedProfile = await onStationDataChange({
        description: profileDraft.description || '',
        photoUrl: profileDraft.photoUrl || '',
      });

      setProfileDraft({
        description:
          savedProfile?.description ??
          profileDraft.description ??
          '',
        photoUrl:
          savedProfile?.photoUrl ??
          profileDraft.photoUrl ??
          '',
      });
    } catch (err) {
      setProfileError(
        err?.message ||
        'Impossible d’enregistrer la description et la photo.'
      );
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleCreateNote = async () => {
    const cleanedContent = noteContent.trim();

    if (!cleanedContent) {
      setActionError('La note ne peut pas être vide.');
      return;
    }

    if (cleanedContent.length > MAX_NOTE_LENGTH) {
      setActionError(
        `La note ne doit pas dépasser ${MAX_NOTE_LENGTH} caractères.`
      );
      return;
    }

    if (!onCreateNote) {
      setActionError('La création de note n’est pas disponible.');
      return;
    }

    setIsSaving(true);
    setActionError('');

    try {
      await onCreateNote({
        content: cleanedContent,
        isPrivate,
      });

      setNoteContent('');
      setIsPrivate(false);
      setIsCreating(false);
    } catch (err) {
      setActionError(
        err?.message ||
        'Impossible de créer la note.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!onDeleteNote || isSaving) {
      return;
    }

    setIsSaving(true);
    setActionError('');

    try {
      await onDeleteNote(noteId);
    } catch (err) {
      setActionError(
        err?.message ||
        'Impossible de supprimer la note.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const displayedError = actionError || profileError || error;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : '94vh',
          borderRadius: isMobile ? 0 : '20px',
          backgroundColor: '#202235',
          backgroundImage: 'none',
          color: '#ffffff',
          border: '1px solid rgba(255,255,255,0.08)',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          position: 'relative',
          padding: {
            xs: '16px 74px 16px 18px',
            sm: '22px 230px 22px 28px',
            md: '22px 250px 22px 28px',
          },
          backgroundColor: '#202235',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: {
              xs: 'flex-start',
              md: 'center',
            },
            gap: '12px',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            paddingRight: '12px',
          }}
        >
          <Typography
            component="h2"
            sx={{
              margin: 0,
              fontSize: {
                xs: '20px',
                md: '24px',
              },
              fontWeight: 800,
              color: '#ffffff',
              lineHeight: 1.2,
            }}
          >
            Notes de la station
          </Typography>

          <Typography
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: '32px',
              padding: '6px 12px',
              borderRadius: '999px',
              backgroundColor: 'rgba(59,130,246,0.12)',
              border: '1px solid rgba(96,165,250,0.32)',
              color: '#dbeafe',
              fontSize: {
                xs: '14px',
                md: '15px',
              },
              fontWeight: 700,
              lineHeight: 1.2,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={stationDisplayName}
          >
            {stationDisplayName}
          </Typography>
        </Box>

        <Typography
          component="p"
          sx={{
            marginTop: '8px',
            marginBottom: 0,
            color: '#94a3b8',
            fontSize: '13px',
          }}
        >
          {stationType} / {stationId}
        </Typography>

        <Box
          sx={{
            position: 'absolute',
            top: {
              xs: '12px',
              md: '18px',
            },
            right: {
              xs: '12px',
              md: '20px',
            },
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Button
            type="button"
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={handleOpenStationDetailsModal}
            disabled={isSaving || isProfileSaving}
            sx={{
              minHeight: '40px',
              display: {
                xs: 'none',
                sm: 'inline-flex',
              },
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              color: '#dbeafe',
              borderColor: 'rgba(96,165,250,0.45)',
              backgroundColor: 'rgba(59,130,246,0.08)',

              '&:hover': {
                borderColor: '#60a5fa',
                backgroundColor: 'rgba(59,130,246,0.16)',
              },
            }}
          >
            Fiche station
          </Button>

          <IconButton
            type="button"
            onClick={handleOpenStationDetailsModal}
            disabled={isSaving || isProfileSaving}
            aria-label="Ouvrir la fiche station"
            sx={{
              display: {
                xs: 'inline-flex',
                sm: 'none',
              },
              color: '#dbeafe',
              backgroundColor: 'rgba(59,130,246,0.12)',

              '&:hover': {
                color: '#ffffff',
                backgroundColor: 'rgba(59,130,246,0.22)',
              },
            }}
          >
            <DescriptionOutlinedIcon />
          </IconButton>

          <IconButton
            type="button"
            onClick={handleClose}
            disabled={isSaving || isProfileSaving}
            aria-label="Fermer la modal de notes"
            sx={{
              color: '#cbd5e1',
              backgroundColor: 'rgba(255,255,255,0.06)',

              '&:hover': {
                color: '#ffffff',
                backgroundColor: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: {
            xs: '18px',
            md: '28px',
          },
          backgroundColor: '#202235',
        }}
      >
        {displayedError && (
          <Alert
            severity="error"
            sx={{
              marginBottom: '18px',
            }}
          >
            {displayedError}
          </Alert>
        )}

        {isLoading ? (
          <Box
            sx={{
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '14px',
            }}
          >
            <CircularProgress size={34} />

            <Typography sx={{ color: '#94a3b8' }}>
              Chargement des notes...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Description et image liées à la fiche station */}
            <Box
              sx={{
                padding: {
                  xs: '16px',
                  md: '20px',
                },
                borderRadius: '16px',
                backgroundColor: '#292b40',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: {
                    xs: 'flex-start',
                    md: 'center',
                  },
                  justifyContent: 'space-between',
                  gap: '14px',
                  flexDirection: {
                    xs: 'column',
                    md: 'row',
                  },
                  marginBottom: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ color: '#60a5fa' }} />

                  <Box>
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: 800,
                        color: '#f8fafc',
                      }}
                    >
                      Description et photo
                    </Typography>

                    <Typography
                      sx={{
                        marginTop: '3px',
                        color: '#94a3b8',
                        fontSize: '13px',
                      }}
                    >
                      Ces informations sont liées à la fiche station.
                    </Typography>
                  </Box>
                </Box>

                <Button
                  type="button"
                  variant="contained"
                  startIcon={
                    isProfileSaving ? (
                      <CircularProgress size={17} color="inherit" />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  disabled={isProfileSaving || isSaving}
                  onClick={handleSaveProfilePart}
                  sx={{
                    minHeight: '40px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700,
                    backgroundColor: '#3b82f6',

                    '&:hover': {
                      backgroundColor: '#2563eb',
                    },
                  }}
                >
                  {isProfileSaving
                    ? 'Enregistrement...'
                    : 'Enregistrer'}
                </Button>
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Description"
                value={profileDraft.description}
                disabled={isProfileSaving}
                onChange={(event) =>
                  handleProfileFieldChange(
                    'description',
                    event.target.value
                  )
                }
                placeholder="Ajouter une description pour cette station..."
                helperText="Cette description est celle affichée dans la fiche station."
                sx={{
                  marginBottom: '20px',

                  '& .MuiInputLabel-root': {
                    color: '#94a3b8',
                  },

                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#60a5fa',
                  },

                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255,255,255,0.04)',

                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.16)',
                    },

                    '&:hover fieldset': {
                      borderColor: 'rgba(96,165,250,0.55)',
                    },

                    '&.Mui-focused fieldset': {
                      borderColor: '#60a5fa',
                    },
                  },

                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                  },
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px',
                }}
              >
                <ImageOutlinedIcon sx={{ color: '#60a5fa' }} />

                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 800,
                    color: '#f8fafc',
                  }}
                >
                  Photo de la station
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  minHeight: {
                    xs: '220px',
                    md: '320px',
                  },
                  borderRadius: '18px',
                  overflow: 'hidden',
                  backgroundColor: '#181a2a',
                  border: '1px dashed rgba(96,165,250,0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {profileDraft.photoUrl ? (
                  <>
                    <Box
                      component="img"
                      src={profileDraft.photoUrl}
                      alt={`Station ${stationId}`}
                      sx={{
                        width: '100%',
                        maxHeight: '420px',
                        objectFit: 'contain',
                      }}
                    />

                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        flexWrap: 'wrap',
                        padding: '16px',
                        backgroundColor: 'rgba(15,23,42,0.52)',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',

                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Button
                        component="label"
                        variant="contained"
                        disabled={isProfileSaving}
                        startIcon={<ImageOutlinedIcon />}
                        sx={{
                          minHeight: '42px',
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 700,
                          backgroundColor: '#3b82f6',

                          '&:hover': {
                            backgroundColor: '#2563eb',
                          },
                        }}
                      >
                        Changer la photo

                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePhotoFileChange}
                        />
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        disabled={isProfileSaving}
                        startIcon={<DeleteOutlineIcon />}
                        onClick={handleRemoveProfilePhoto}
                        sx={{
                          minHeight: '42px',
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 700,
                          color: '#fecaca',
                          borderColor: 'rgba(248,113,113,0.7)',
                          backgroundColor: 'rgba(127,29,29,0.28)',

                          '&:hover': {
                            borderColor: '#f87171',
                            backgroundColor: 'rgba(127,29,29,0.45)',
                          },
                        }}
                      >
                        Supprimer
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box
                    component="label"
                    sx={{
                      width: '100%',
                      minHeight: {
                        xs: '220px',
                        md: '320px',
                      },
                      padding: '30px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      color: '#64748b',
                      cursor: isProfileSaving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ImageOutlinedIcon
                      sx={{
                        fontSize: '70px',
                        color: '#60a5fa',
                      }}
                    />

                    <Typography
                      sx={{
                        marginTop: '10px',
                        color: '#dbeafe',
                        fontWeight: 700,
                      }}
                    >
                      Importer une photo
                    </Typography>

                    <Typography
                      sx={{
                        marginTop: '5px',
                        maxWidth: '420px',
                        fontSize: '13px',
                        color: '#64748b',
                      }}
                    >
                      Cliquez dans cette zone pour choisir une image depuis votre ordinateur.
                    </Typography>

                    <Button
                      component="span"
                      variant="outlined"
                      disabled={isProfileSaving}
                      startIcon={<ImageOutlinedIcon />}
                      sx={{
                        marginTop: '18px',
                        minHeight: '42px',
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 700,
                        color: '#dbeafe',
                        borderColor: 'rgba(96,165,250,0.55)',

                        '&:hover': {
                          borderColor: '#60a5fa',
                          backgroundColor: 'rgba(59,130,246,0.12)',
                        },
                      }}
                    >
                      Choisir une photo
                    </Button>

                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePhotoFileChange}
                    />
                  </Box>
                )}
              </Box>
            </Box>


            {/* Liste des notes */}
            <Box
              sx={{
                marginTop: '26px',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                gap: '16px',
              }}
            >
              {/* Bouton d'ajout de note */}
              {!isCreating ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setIsCreating(true)}
                  disabled={isSaving}
                  sx={{
                    minHeight: '210px',
                    borderRadius: '16px',
                    border: '1px dashed rgba(96,165,250,0.75)',
                    backgroundColor: 'rgba(59,130,246,0.08)',
                    color: '#dbeafe',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease',

                    '&:hover': {
                      borderColor: '#60a5fa',
                      backgroundColor: 'rgba(59,130,246,0.16)',
                    },

                    '&:disabled': {
                      opacity: 0.55,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <AddRoundedIcon sx={{ fontSize: '48px' }} />

                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '16px',
                    }}
                  >
                    Ajouter une note
                  </Typography>

                  <Typography
                    sx={{
                      maxWidth: '240px',
                      color: '#94a3b8',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  >
                    1000 caractères maximum. La note peut être publique ou privée.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    minHeight: '210px',
                    padding: '16px',
                    borderRadius: '16px',
                    backgroundColor: '#fef3c7',
                    color: '#1f2937',
                    border: '1px solid rgba(245,158,11,0.55)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 800,
                      marginBottom: '10px',
                    }}
                  >
                    Nouvelle note
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    value={noteContent}
                    onChange={(event) => {
                      if (event.target.value.length <= MAX_NOTE_LENGTH) {
                        setNoteContent(event.target.value);
                      }
                    }}
                    placeholder="Écrire une note..."
                    disabled={isSaving}
                    inputProps={{
                      maxLength: MAX_NOTE_LENGTH,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255,255,255,0.55)',
                        borderRadius: '12px',
                        color: '#111827',

                        '& fieldset': {
                          borderColor: 'rgba(146,64,14,0.25)',
                        },

                        '&:hover fieldset': {
                          borderColor: 'rgba(146,64,14,0.45)',
                        },

                        '&.Mui-focused fieldset': {
                          borderColor: '#d97706',
                        },
                      },
                    }}
                  />

                  <Box
                    sx={{
                      marginTop: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#92400e',
                      }}
                    >
                      {noteContent.length}/{MAX_NOTE_LENGTH}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {isPrivate ? (
                        <LockOutlinedIcon
                          sx={{
                            fontSize: '18px',
                            color: '#92400e',
                          }}
                        />
                      ) : (
                        <LockOpenOutlinedIcon
                          sx={{
                            fontSize: '18px',
                            color: '#92400e',
                          }}
                        />
                      )}

                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#92400e',
                          fontWeight: 700,
                        }}
                      >
                        {isPrivate ? 'Privée' : 'Publique'}
                      </Typography>

                      <Switch
                        checked={isPrivate}
                        onChange={(event) =>
                          setIsPrivate(event.target.checked)
                        }
                        disabled={isSaving}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      marginTop: '14px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '8px',
                    }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={isSaving}
                      onClick={() => {
                        setIsCreating(false);
                        setNoteContent('');
                        setIsPrivate(false);
                        setActionError('');
                      }}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                      }}
                    >
                      Annuler
                    </Button>

                    <Button
                      type="button"
                      variant="contained"
                      disabled={isSaving || !noteContent.trim()}
                      onClick={handleCreateNote}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                      }}
                    >
                      {isSaving ? 'Ajout...' : 'Ajouter'}
                    </Button>
                  </Box>
                </Box>
              )}

              {notes.map((note) => (
                <Box
                  key={note.id}
                  sx={{
                    minHeight: '210px',
                    padding: '16px',
                    borderRadius: '16px',
                    backgroundColor: note.isPrivate
                      ? '#fee2e2'
                      : '#fef3c7',
                    color: '#1f2937',
                    border: note.isPrivate
                      ? '1px solid rgba(248,113,113,0.7)'
                      : '1px solid rgba(245,158,11,0.45)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '10px',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: '14px',
                        }}
                      >
                        {formatNoteDate(note.createdAt)}
                      </Typography>

                      <Chip
                        size="small"
                        icon={
                          note.isPrivate ? (
                            <LockOutlinedIcon />
                          ) : (
                            <LockOpenOutlinedIcon />
                          )
                        }
                        label={note.isPrivate ? 'Privée' : 'Publique'}
                        sx={{
                          marginTop: '6px',
                          height: '24px',
                          fontSize: '11px',
                          backgroundColor: note.isPrivate
                            ? 'rgba(127,29,29,0.12)'
                            : 'rgba(146,64,14,0.12)',
                          color: note.isPrivate
                            ? '#7f1d1d'
                            : '#92400e',

                          '& .MuiChip-icon': {
                            color: 'inherit',
                            fontSize: '15px',
                          },
                        }}
                      />
                    </Box>

                    <IconButton
                      type="button"
                      disabled={isSaving}
                      onClick={() => handleDeleteNote(note.id)}
                      title="Supprimer la note"
                      sx={{
                        color: '#7f1d1d',
                        backgroundColor: 'rgba(127,29,29,0.08)',

                        '&:hover': {
                          backgroundColor: 'rgba(127,29,29,0.16)',
                        },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography
                    sx={{
                      marginTop: '14px',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {note.content}
                  </Typography>
                </Box>
              ))}
            </Box>

            {notes.length === 0 && !isCreating && (
              <Box
                sx={{
                  marginTop: '16px',
                  padding: '18px',
                  borderRadius: '14px',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography
                  sx={{
                    color: '#94a3b8',
                    textAlign: 'center',
                    fontSize: '14px',
                  }}
                >
                  Aucune note n’a encore été ajoutée pour cette station.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StationNotesModal;